import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { listFeeTiers, upsertFeeTier, deleteFeeTier, listFeeTierAuditLog, type FeeTierAuditEntry } from "@/lib/fee-tiers.functions";

export const Route = createFileRoute("/_authenticated/admin/fee-tiers")({
  head: () => ({ meta: [{ title: "Platform fee tiers — Admin" }] }),
  component: AdminFeeTiersPage,
});

type Tier = {
  id: string;
  min_workers: number;
  max_workers: number | null;
  fee_pence: number;
  sort_order: number;
};

type Draft = {
  min_workers: string;
  max_workers: string; // "" → unbounded
  fee_pounds: string;
  sort_order: string;
};

function toDraft(t: Tier): Draft {
  return {
    min_workers: String(t.min_workers),
    max_workers: t.max_workers === null ? "" : String(t.max_workers),
    fee_pounds: (t.fee_pence / 100).toFixed(2),
    sort_order: String(t.sort_order),
  };
}

function fromDraft(d: Draft) {
  const min = Math.floor(Number(d.min_workers));
  const maxRaw = d.max_workers.trim();
  const max = maxRaw === "" ? null : Math.floor(Number(maxRaw));
  const fee_pence = Math.round(Number(d.fee_pounds) * 100);
  const sort_order = Math.floor(Number(d.sort_order));
  return { min_workers: min, max_workers: max, fee_pence, sort_order };
}

function AdminFeeTiersPage() {
  const fetchTiers = useServerFn(listFeeTiers);
  const save = useServerFn(upsertFeeTier);
  const remove = useServerFn(deleteFeeTier);
  const fetchAudit = useServerFn(listFeeTierAuditLog);

  const [tiers, setTiers] = useState<Tier[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [newDraft, setNewDraft] = useState<Draft>({ min_workers: "", max_workers: "", fee_pounds: "", sort_order: "" });
  const [creating, setCreating] = useState(false);
  const [auditEntries, setAuditEntries] = useState<FeeTierAuditEntry[]>([]);

  const reload = async () => {
    try {
      const [r, a] = await Promise.all([fetchTiers(), fetchAudit()]);
      setTiers(r.tiers as Tier[]);
      const next: Record<string, Draft> = {};
      for (const t of r.tiers as Tier[]) next[t.id] = toDraft(t);
      setDrafts(next);
      setAuditEntries(a.entries);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onSave = async (t: Tier) => {
    const d = drafts[t.id];
    if (!d) return;
    setSavingId(t.id);
    try {
      await save({ data: { id: t.id, ...fromDraft(d) } });
      toast.success("Tier saved");
      await reload();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (t: Tier) => {
    if (!confirm(`Delete the ${t.min_workers}${t.max_workers ? `–${t.max_workers}` : "+"} staff tier?`)) return;
    try {
      await remove({ data: { id: t.id } });
      toast.success("Tier removed");
      await reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const onCreate = async () => {
    setCreating(true);
    try {
      await save({ data: fromDraft(newDraft) });
      toast.success("Tier added");
      setNewDraft({ min_workers: "", max_workers: "", fee_pounds: "", sort_order: "" });
      await reload();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <header className="mb-8">
          <p className="tracking-wide-luxury text-[10px] uppercase text-accent">Admin · Pricing</p>
          <h1 className="mt-2 font-serif text-3xl">Platform fee tiers</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Edit the per-worker platform fee bands shown on the venue posting page. Fees are locked into each
            shift at posting time, so changes here only affect <em>new</em> shifts going forward — historic rows
            keep the tier they paid.
          </p>
        </header>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading tiers…</p>
        ) : (
          <>
            <div className="border border-border">
              <div className="grid grid-cols-12 gap-2 border-b border-border bg-muted/30 px-4 py-3 text-[10px] uppercase tracking-wide-luxury text-muted-foreground">
                <div className="col-span-2">Min staff</div>
                <div className="col-span-2">Max staff</div>
                <div className="col-span-3">Fee (£/worker)</div>
                <div className="col-span-2">Sort</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>
              {tiers.length === 0 && (
                <p className="px-4 py-6 text-sm text-muted-foreground">No tiers configured.</p>
              )}
              {tiers.map((t) => {
                const d = drafts[t.id] ?? toDraft(t);
                const set = (patch: Partial<Draft>) => setDrafts((all) => ({ ...all, [t.id]: { ...d, ...patch } }));
                return (
                  <div key={t.id} className="grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3">
                    <Input className="col-span-2 h-9" type="number" min="1" value={d.min_workers}
                      onChange={(e) => set({ min_workers: e.target.value })} />
                    <Input className="col-span-2 h-9" type="number" min="1" placeholder="∞"
                      value={d.max_workers} onChange={(e) => set({ max_workers: e.target.value })} />
                    <Input className="col-span-3 h-9" type="number" min="0" step="0.01" value={d.fee_pounds}
                      onChange={(e) => set({ fee_pounds: e.target.value })} />
                    <Input className="col-span-2 h-9" type="number" value={d.sort_order}
                      onChange={(e) => set({ sort_order: e.target.value })} />
                    <div className="col-span-3 flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => onDelete(t)} disabled={savingId === t.id}>
                        Delete
                      </Button>
                      <Button size="sm" onClick={() => onSave(t)} disabled={savingId === t.id}>
                        {savingId === t.id ? "Saving…" : "Save"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <section className="mt-10 border border-border bg-muted/20 p-6">
              <h2 className="font-serif text-xl">Add a new tier</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Leave <em>Max staff</em> empty for an unbounded top tier (e.g. 20+).
              </p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div>
                  <Label htmlFor="new-min">Min staff</Label>
                  <Input id="new-min" type="number" min="1" className="mt-2" value={newDraft.min_workers}
                    onChange={(e) => setNewDraft({ ...newDraft, min_workers: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="new-max">Max staff</Label>
                  <Input id="new-max" type="number" min="1" placeholder="∞" className="mt-2" value={newDraft.max_workers}
                    onChange={(e) => setNewDraft({ ...newDraft, max_workers: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="new-fee">Fee (£/worker)</Label>
                  <Input id="new-fee" type="number" min="0" step="0.01" className="mt-2" value={newDraft.fee_pounds}
                    onChange={(e) => setNewDraft({ ...newDraft, fee_pounds: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="new-sort">Sort</Label>
                  <Input id="new-sort" type="number" className="mt-2" value={newDraft.sort_order}
                    onChange={(e) => setNewDraft({ ...newDraft, sort_order: e.target.value })} />
                </div>
              </div>
              <div className="mt-4">
                <Button onClick={onCreate} disabled={creating}>
                  {creating ? "Adding…" : "Add tier"}
                </Button>
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-serif text-xl">Change history</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Most recent 100 changes. Use this to trace pricing discrepancies back to a specific edit.
              </p>
              <div className="mt-4 border border-border">
                <div className="grid grid-cols-12 gap-2 border-b border-border bg-muted/30 px-4 py-3 text-[10px] uppercase tracking-wide-luxury text-muted-foreground">
                  <div className="col-span-3">When</div>
                  <div className="col-span-2">Action</div>
                  <div className="col-span-2">Admin</div>
                  <div className="col-span-5">Change</div>
                </div>
                {auditEntries.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-muted-foreground">No changes recorded yet.</p>
                ) : auditEntries.map((e) => {
                  const fmt = (v: FeeTierAuditEntry["old_values"]) => {
                    if (!v) return "—";
                    const min = v.min_workers ?? "?";
                    const max = v.max_workers === null || v.max_workers === undefined ? "∞" : v.max_workers;
                    const fee = v.fee_pence !== undefined ? `£${(v.fee_pence / 100).toFixed(2)}` : "—";
                    return `${min}–${max} · ${fee}`;
                  };
                  return (
                    <div key={e.id} className="grid grid-cols-12 gap-2 border-b border-border px-4 py-3 text-xs">
                      <div className="col-span-3 text-muted-foreground">{new Date(e.created_at).toLocaleString()}</div>
                      <div className="col-span-2 font-medium uppercase">{e.action}</div>
                      <div className="col-span-2 truncate">{e.actor_name || e.actor_user_id?.slice(0, 8) || "system"}</div>
                      <div className="col-span-5">
                        <span className="text-muted-foreground">{fmt(e.old_values)}</span>
                        {" → "}
                        <span className="text-foreground">{fmt(e.new_values)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
